import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';

const NotificationBell = ({ tabBarMode = false }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // openUpward: true when rendered inside the bottom tab bar so the panel
  // appears above the bar rather than below the bell icon.
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, openUpward: false });
  const [feedbackModal, setFeedbackModal] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  const getToken = () => safeStorage.getItem('authToken');

  // Fetch notification list when panel opens
  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll the unread count every 30 s.  Each request opens a connection,
  // gets the count, and closes — no long-lived connections held open.
  useEffect(() => {
    if (!getToken()) return;

    let cancelled = false;

    const fetchCount = async () => {
      // Read the token fresh on every poll so that a re-login after session
      // expiry uses the new token rather than the stale one captured at mount.
      const currentToken = getToken();
      if (!currentToken) return; // logged out — skip silently, don't send a 401
      try {
        const res = await fetch(`${API_URL}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        if (!res.ok) { res.body?.cancel(); return; }
        const data = await res.json();
        if (!cancelled) setUnreadCount(data.count);
      } catch (_) { /* silent — will retry on next poll */ }
    };

    fetchCount();
    const intervalId = setInterval(fetchCount, 30_000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const togglePanel = () => {
    const opening = !isOpen;
    if (opening && bellRef.current) {
      const panelWidth = Math.min(360, window.innerWidth - 32);
      if (tabBarMode) {
        // Panel opens upward from the bottom tab bar — center it on the screen.
        setPanelPos({
          top: null,
          left: Math.max(8, Math.round((window.innerWidth - panelWidth) / 2)),
          openUpward: true,
        });
      } else {
        // Normal mode: panel drops down below the bell icon, right-aligned.
        const rect = bellRef.current.getBoundingClientRect();
        setPanelPos({
          top: rect.bottom + 4,
          left: Math.max(8, rect.right - panelWidth),
          openUpward: false,
        });
      }
    }
    setIsOpen(opening);
    if (opening) fetchNotifications();
  };

  const markAsRead = async (notificationId) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${API_URL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  const markAllRead = async () => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${API_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${API_URL}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const removed = notifications.find(n => n.notification_id === notificationId);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      if (removed && !removed.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const clearAllRead = async () => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${API_URL}/notifications/clear-all`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => !n.is_read));
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'moderation': return '🛡️';
      case 'wiki': return '✏️';
      case 'system': return '⚙️';
      case 'info': return 'ℹ️';
      case 'feedback': return '📝';
      default: return '🔔';
    }
  };

  const fetchMyFeedback = async (feedbackId) => {
    const token = getToken();
    if (!token) return;
    setFeedbackLoading(true);
    try {
      const res = await fetch(`${API_URL}/feedback/my/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFeedbackModal(data);
      } else {
        setFeedbackModal({ error: 'Could not load feedback details.' });
      }
    } catch {
      setFeedbackModal({ error: 'Could not load feedback details.' });
    } finally {
      setFeedbackLoading(false);
    }
  };

  const statusLabels = {
    new: 'New',
    reviewed: 'Reviewed',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    declined: 'Declined'
  };

  const categoryLabels = {
    bug: '🐛 Bug',
    feature_request: '✨ Feature Request',
    ui_ux: '🎨 UI/UX',
    general: '💬 General'
  };

  return (
    <div style={{ position: 'relative', display: tabBarMode ? 'flex' : 'block',
                  flex: tabBarMode ? 1 : undefined, width: tabBarMode ? '100%' : undefined }}>
      {/* Bell button — renders as a tab slot in tabBarMode, icon button otherwise */}
      <button
        ref={bellRef}
        onClick={togglePanel}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        className={tabBarMode ? `btb-tab${isOpen ? ' btb-tab--active' : ''}` : undefined}
        style={tabBarMode ? {
          // Let the .btb-tab CSS class do most of the work; just reset button defaults
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          flex: 1,
          width: '100%',
          position: 'relative',
        } : {
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          padding: '6px',
          fontSize: '20px',
          lineHeight: 1,
          color: isOpen ? '#2563eb' : '#4a5568',
          transition: 'color 0.2s'
        }}
      >
        {tabBarMode ? (
          // Tab slot layout: icon with badge + label underneath
          <>
            <span className="btb-icon" style={{ position: 'relative', display: 'inline-block' }}>
              🔔
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-8px',
                  background: '#e53e3e',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: 700,
                  minWidth: '14px',
                  height: '14px',
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 3px',
                  lineHeight: 1
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </span>
            <span className="btb-label">Alerts</span>
          </>
        ) : (
          // Normal header/sidebar bell
          <>
            🔔
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '0',
                right: '0',
                background: '#e53e3e',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 700,
                minWidth: '16px',
                height: '16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                lineHeight: 1
              }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div ref={dropdownRef} style={{
          position: 'fixed',
          // Open upward (tabBarMode) or downward (default)
          ...(panelPos.openUpward
            ? { bottom: 64 }
            : { top: panelPos.top }
          ),
          left: panelPos.left,
          width: 'min(360px, calc(100vw - 32px))',
          maxHeight: panelPos.openUpward
            ? 'min(480px, calc(100vh - 80px))'
            : 'min(480px, calc(100vh - ' + ((panelPos.top || 0) + 16) + 'px))',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e2e8f0'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 16px',
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc'
          }}>
            <span style={{ fontWeight: 700, fontSize: '15px', color: '#1a202c' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Mark all read
              </button>
            )}
            {unreadCount === 0 && notifications.length > 0 && notifications.every(n => n.is_read) && (
              <button
                onClick={clearAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#e53e3e',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Clear all
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#a0aec0' }}>
                Loading…
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#a0aec0' }}>
                No notifications yet
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.notification_id}
                  onClick={() => {
                    if (!n.is_read) markAsRead(n.notification_id);
                    if (n.type === 'feedback') {
                      // Extract feedbackId from metadata, title (#N pattern), or action_url query param
                      const feedbackId = n.metadata?.feedback_id
                        || n.title?.match(/#(\d+)/)?.[1]
                        || n.action_url?.match(/[?&]feedbackId=(\d+)/)?.[1];
                      setIsOpen(false);
                      if (isAdmin) {
                        navigate(feedbackId ? `/feedback-admin?feedbackId=${feedbackId}` : '/feedback-admin');
                      } else if (feedbackId) {
                        fetchMyFeedback(feedbackId);
                      }
                    } else if (n.action_url) {
                      setIsOpen(false);
                      navigate(n.action_url);
                    }
                  }}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    background: n.is_read ? '#fff' : '#f0f7ff',
                    cursor: (n.type === 'feedback' || n.action_url || !n.is_read) ? 'pointer' : 'default',
                    transition: 'background 0.15s'
                  }}
                >
                  <div style={{ fontSize: '18px', flexShrink: 0, paddingTop: '2px' }}>
                    {getTypeIcon(n.type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: n.is_read ? 400 : 600,
                      fontSize: '13px',
                      color: '#1a202c',
                      marginBottom: '2px'
                    }}>
                      {n.title}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#718096',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                      overflow: 'hidden'
                    }}>
                      {n.message}
                    </div>
                    <div style={{ fontSize: '11px', color: '#a0aec0', marginTop: '4px' }}>
                      {formatTime(n.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteNotification(n.notification_id); }}
                    title="Dismiss"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#cbd5e0',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '2px 4px',
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                      transition: 'color 0.15s'
                    }}
                    onMouseEnter={e => e.target.style.color = '#e53e3e'}
                    onMouseLeave={e => e.target.style.color = '#cbd5e0'}
                  >
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Read-only feedback detail modal for non-admin users */}
      {(feedbackModal || feedbackLoading) && (
        <div
          onClick={() => { setFeedbackModal(null); setFeedbackLoading(false); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px', padding: '24px',
              width: 'min(520px, calc(100vw - 48px))', maxHeight: 'calc(100vh - 96px)',
              overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            {feedbackLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Loading…</div>
            ) : feedbackModal?.error ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#e53e3e' }}>{feedbackModal.error}</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#1a202c' }}>
                    Feedback #{feedbackModal.feedback_id}
                  </h3>
                  <button
                    onClick={() => setFeedbackModal(null)}
                    style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#a0aec0', padding: '4px' }}
                  >✕</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px', fontSize: '13px' }}>
                  <div><strong>Category:</strong> {categoryLabels[feedbackModal.category] || feedbackModal.category}</div>
                  <div><strong>Status:</strong> {statusLabels[feedbackModal.status] || feedbackModal.status}</div>
                  <div><strong>Submitted:</strong> {new Date(feedbackModal.created_at).toLocaleDateString()}</div>
                  {feedbackModal.resolved_at && (
                    <div><strong>Resolved:</strong> {new Date(feedbackModal.resolved_at).toLocaleDateString()}</div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Your Submission
                  </label>
                  <div style={{
                    marginTop: '4px', padding: '12px', backgroundColor: '#f7fafc', borderRadius: '6px',
                    border: '1px solid #e2e8f0', fontSize: '13px', color: '#333',
                    lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto',
                  }}>
                    {feedbackModal.description}
                  </div>
                </div>

                {feedbackModal.admin_notes && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Team Notes
                    </label>
                    <div style={{
                      marginTop: '4px', padding: '12px', backgroundColor: '#fffbeb', borderRadius: '6px',
                      border: '1px solid #fbd38d', fontSize: '13px', color: '#744210',
                      lineHeight: 1.6, whiteSpace: 'pre-wrap',
                    }}>
                      {feedbackModal.admin_notes}
                    </div>
                  </div>
                )}

                {(feedbackModal.change_history || []).length > 0 && (
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      History
                    </label>
                    <div style={{
                      marginTop: '4px', backgroundColor: '#f7fafc', borderRadius: '6px',
                      border: '1px solid #e2e8f0', maxHeight: '180px', overflowY: 'auto',
                    }}>
                      {[...feedbackModal.change_history].reverse().map((entry, idx) => (
                        <div key={idx} style={{
                          padding: '8px 12px', fontSize: '12px',
                          borderBottom: idx < feedbackModal.change_history.length - 1 ? '1px solid #f0f0f0' : 'none',
                        }}>
                          <span style={{ fontWeight: 600, color: '#333' }}>
                            {statusLabels[entry.to_status] || entry.to_status}
                          </span>
                          <span style={{ color: '#a0aec0', marginLeft: '8px', fontSize: '11px' }}>
                            {new Date(entry.timestamp).toLocaleDateString()}
                          </span>
                          {entry.notes && (
                            <div style={{ color: '#555', fontStyle: 'italic', marginTop: '2px' }}>"{entry.notes}"</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
